import i18n from '@opsbase/lib-i18n';
import useSelf from '@opsbase/react-ctrl-self/useSelf';
import useSelfActions from '@opsbase/react-ctrl-self/useSelfActions/useSelfActions';
import useIQMTestConnection from '@opsbase/react-gql-hooks/integration-iqm/useIQMTestConnection';
import useOrgUpdate from '@opsbase/react-gql-hooks/organisation/useOrgUpdate';
import markdown from '@opsbase/react-markdown';
import AppPage from '@opsbase/web-app/AppPage';
import useCss from '@opsbase/web-css';
import Card from '@opsbase/web-layout/Card';
import Divider from '@opsbase/web-layout/Divider';
import Horizontal from '@opsbase/web-layout/Horizontal';
import Pane from '@opsbase/web-layout/Pane';
import Vertical from '@opsbase/web-layout/Vertical';
import TextFieldWithPasswordShowHide from '@opsbase/web-textfield/TextFieldWithPasswordShowHide';
import TextFieldWithStateAndBlur from '@opsbase/web-textfield/TextFieldWithStateAndBlur';
import Button from '@opsbase/web-ui/Button';
import Text from '@opsbase/web-ui/Text';
import React, { useCallback, useEffect, useRef, useState } from 'react';
const makeMyClasses = () => ({
    textBox: {
        minWidth: 300,
    },
});
// eslint-disable-next-line no-shadow
var TESTING;
(function (TESTING) {
    TESTING[TESTING["NONE"] = 0] = "NONE";
    TESTING[TESTING["PROCESSING"] = 1] = "PROCESSING";
    TESTING[TESTING["SUCCEEDED"] = 2] = "SUCCEEDED";
    TESTING[TESTING["FAILED"] = 3] = "FAILED";
})(TESTING || (TESTING = {}));
export function AdvancedIntegrationsIQMPage() {
    var _a, _b;
    const self = useSelf();
    const { updateSettings } = useSelfActions();
    const updateOrg = useOrgUpdate();
    const [testing, setTesting] = useState(TESTING.NONE);
    const [iqm, setIQM] = useState((_b = (_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.integrations) === null || _b === void 0 ? void 0 : _b.iqm);
    const initialMount = useRef(true);
    useEffect(() => {
        var _a, _b;
        if (!initialMount.current) {
            setIQM((_b = (_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.integrations) === null || _b === void 0 ? void 0 : _b.iqm);
        }
        else {
            initialMount.current = false;
        }
    }, [self]);
    const myCss = useCss(makeMyClasses);
    const testIQM = useIQMTestConnection();
    const { domain, userAgent, apiKey } = iqm || {};
    const testEnabled = userAgent && apiKey;
    const handleChange = useCallback(field => value => {
        var _a, _b;
        if (field === 'domain' && value !== '') {
            try {
                if (!value.startsWith('http://') && !value.startsWith('https://')) {
                    value = `https://${value}`;
                }
                const url = new URL(value);
                value = url.hostname;
            }
            catch (error) {
                console.error(`The domain ${value} is not valid. ${error}`);
                return;
            }
        }
        const iqmSettings = {
            ...(_b = (_a = self === null || self === void 0 ? void 0 : self.settings) === null || _a === void 0 ? void 0 : _a.integrations) === null || _b === void 0 ? void 0 : _b.iqm,
            [field]: value,
        };
        setIQM(iqmSettings);
        return updateOrg({
            id: self.organisationId,
            patch: { settings: { integrations: { iqm: iqmSettings } } },
        }).then(updatedOrg => updateSettings(updatedOrg.settings));
    }, [self, updateOrg, updateSettings]);
    const handleTestPressed = useCallback(async () => {
        setTesting(TESTING.PROCESSING);
        try {
            const testResult = await testIQM();
            if (testResult) {
                setTesting(TESTING.SUCCEEDED);
            }
            else {
                setTesting(TESTING.FAILED);
            }
        }
        catch (_a) {
            setTesting(TESTING.FAILED);
        }
    }, [testIQM]);
    return (React.createElement(AppPage, null,
        React.createElement(Vertical, { gutter: true, padding: true, style: { overflow: 'auto' } },
            React.createElement(Card, { padding: true, style: { maxWidth: 500 } },
                React.createElement(Horizontal, { centered: true }),
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('iqm-domain'))),
                    React.createElement(TextFieldWithStateAndBlur, { className: myCss.textBox, value: domain, onBlur: handleChange('domain') })),
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('iqm-user-agent'))),
                    React.createElement(TextFieldWithStateAndBlur, { className: myCss.textBox, value: userAgent, onBlur: handleChange('userAgent') })),
                React.createElement(Horizontal, { centered: true },
                    React.createElement(Text, { variant: "h3" }, markdown(i18n.t('iqm-api-key'))),
                    React.createElement(TextFieldWithPasswordShowHide, { className: myCss.textBox, value: apiKey, onBlur: handleChange('apiKey') })),
                React.createElement(Pane, { right: true, padding: true },
                    React.createElement(Button, { label: i18n.t('test'), onClick: handleTestPressed, style: { marginRight: 8 }, disabled: !testEnabled, loading: testing === TESTING.PROCESSING })),
                (testing === TESTING.SUCCEEDED || testing === TESTING.FAILED) && (React.createElement(Vertical, { style: {
                        margin: 16,
                        padding: 16,
                    } },
                    testing === TESTING.SUCCEEDED && (React.createElement(Text, { variant: "h2" }, i18n.t('iqm-integration-test-success'))),
                    testing === TESTING.FAILED && (React.createElement(Text, { variant: "h2" }, i18n.t('iqm-integration-test-failure'))))),
                React.createElement(Divider, null),
                React.createElement(Text, { variant: "body" }, markdown(i18n.t('iqm-integration-intro')))))));
}
export default AdvancedIntegrationsIQMPage;
